import { Button, Modal } from "react-bootstrap";
import { memo, useEffect, useMemo, useState } from "react";
import { DownloadOptions, DownloadPayload } from "types";
import { HttpStatus } from "constant";
import { isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { downloadFastStorage, downloadStorage } from "service/gatewayService";
import { APP_CONFIG } from "config/appConfig";
import { extractDownloadFilename } from "utils/gatewayFunctions";

interface Props {
    show: boolean;
    downloadType?: DownloadOptions;
    onClose?: () => void;
    onConfirm?: (
        confirmCallback: (data: DownloadPayload) => Promise<void> | void
    ) => Promise<void>;
}

// Utility function to trigger file download
const triggerFileDownload = (blob: Blob, fileName: string): void => {
    const link = document.createElement("a");
    const url = globalThis.URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName;
    link.click();

    globalThis.URL.revokeObjectURL(url);
};

// Get messages based on download option and environment
const getMessages = (downloadOption: DownloadOptions, isOnPrem: boolean) => {
    const isEmail = downloadOption === "email";
    const deliveryMethod = isOnPrem ? "notification" : "email";

    if (isEmail) {
        return {
            confirmation: `By confirming, you'll receive ${
                isOnPrem ? "a" : "an"
            } ${deliveryMethod} with the option to download the file.`,
            success: `${
                isOnPrem ? "A" : "An"
            } ${deliveryMethod} will be sent to you shortly once it is ready to download.`,
        };
    }

    return {
        confirmation: "By confirming, data will be ready for download shortly.",
        success: "Data for the selected range will be downloaded.",
    };
};

const ConfirmDownloadModalComponent = (props: Props) => {
    const [showModal, setShowModal] = useState(false);
    const { show, downloadType = "email", onClose, onConfirm } = props;
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadOption, setDownloadOption] =
        useState<DownloadOptions>("email");

    const messages = useMemo(
        () => getMessages(downloadOption, APP_CONFIG.APP_IS_ONPREM),
        [downloadOption]
    );

    const handleConfirmClick = async (e: any) => {
        e.preventDefault();
        setIsDownloading(true);
        onConfirm?.((data) => downloadData(data));
    };

    const downloadData = async (data: DownloadPayload) => {
        const res = await (downloadOption === "fast"
            ? downloadFastStorage(data)
            : downloadStorage(data));

        if (isHttpSuccess(res.status)) {
            if (downloadOption === "email") {
                showSuccessAlert({ message: messages.success });
            } else {
                const headers = "headers" in res ? res.headers : undefined;
                const fileName = extractDownloadFilename(headers);
                triggerFileDownload(res.data, fileName);
                showSuccessAlert({ message: messages.success });
            }
        } else if (res.status === HttpStatus.TOO_MANY_REQUESTS) {
            showErrorAlert({
                title: "Error",
                message: "Server is busy. Please try again later.",
            });
        } else {
            showErrorAlert(
                getAPIError(res, "Unable to download data. Please try again.")
            );
        }
        handleCloseModal();
    };

    const handleCloseModal = () => {
        onClose?.();
        setIsDownloading(false);
    };

    useEffect(() => {
        setShowModal(show);
        setIsDownloading(false);
    }, [show]);

    useEffect(() => {
        setDownloadOption(downloadType);
    }, [downloadType]);

    return (
        <Modal
            centered
            show={showModal}
            onHide={handleCloseModal}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-sm"
            className="no-header primary"
        >
            <Modal.Body className="text-center mt-3 mb-3">
                <div className="modal-icon-box">
                    <span className="material-icons outlined">
                        help_outline
                    </span>
                </div>
                <h3 className="mb-3">Confirmation</h3>
                <p className="mb-4">
                    {messages.confirmation}
                    <br />
                    Do you want to continue?
                </p>

                <Button variant="danger" onClick={handleCloseModal}>
                    NO
                </Button>
                <Button
                    disabled={isDownloading || !showModal}
                    variant="primary"
                    onClick={handleConfirmClick}
                >
                    YES
                </Button>
            </Modal.Body>
        </Modal>
    );
};

export const ConfirmDownloadModal = memo(ConfirmDownloadModalComponent);

